/*----------------------------------
- DOMAINING NEWS CATEGORY FILTERS
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import {
    USER_PREFERENCE_NEWS_TYPE_IDS,
    type UserPreferenceNewsTypeId,
} from '@uniquedomains/components/contracts/users/preferences';
import { getDomainingNewsCategoryShortLabel } from '@uniquedomains/components/news/presentation';

import { buildDomainingNewsArchivePath } from '../../lib/presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainingNewsCategoryFiltersProps = {
    /** The filter the backend actually applied, echoed by the list response. */
    activeCategoryCode: UserPreferenceNewsTypeId | null;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Filter chips. Every chip is a real link back through the archive route, so the
 * filter is applied by the backend query and the counters, pagination and
 * structured data all describe the rows actually shown.
 */
export default function DomainingNewsCategoryFilters({
    activeCategoryCode,
}: DomainingNewsCategoryFiltersProps) {
    const chips: Array<{ code: UserPreferenceNewsTypeId | null; label: string }> = [
        { code: null, label: 'All' },
        ...USER_PREFERENCE_NEWS_TYPE_IDS.map((code) => ({
            code,
            label: getDomainingNewsCategoryShortLabel(code),
        })),
    ];

    return (
        // The chip row scrolls sideways on a phone and wraps from `lg` up, so eleven
        // categories never push the first edition below the fold.
        <div
            class="-mx-5 flex gap-1.5 overflow-x-auto px-5 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden"
            data-testid="domaining-news-category-filters"
        >
            {chips.map((chip) => {
                const isActive = chip.code === activeCategoryCode;
                return (
                    <a
                        key={chip.code ?? 'all'}
                        href={buildDomainingNewsArchivePath(1, chip.code)}
                        aria-current={isActive ? 'true' : undefined}
                        class={[
                            'inline-flex h-9 flex-none items-center whitespace-nowrap rounded-full px-3.5 text-[12.5px] transition lg:h-[38px] lg:px-4 lg:text-[13px]',
                            isActive
                                ? 'bg-[#1b1d1e] font-bold text-white'
                                : 'border border-[rgba(27,29,30,0.12)] font-semibold text-[#66696b] hover:border-[rgba(27,29,30,0.3)] hover:text-[#1b1d1e]',
                        ].join(' ')}
                    >
                        {chip.label}
                    </a>
                );
            })}
        </div>
    );
}
